/**
 * AB#9981. Element construction, deliberately tiny.
 *
 * Everything here builds DOM nodes and sets text through `textContent`. Nothing in
 * this module accepts an HTML string, and no caller may hand one in, because every
 * value this component renders -- speaker names, organisations, agenda titles -- is
 * database-sourced text typed by a member of the public into a government
 * registration form. `innerHTML` on any of it is a stored XSS.
 */

type Attrs = Record<string, string | number | boolean | null | undefined>;

export function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  attrs: Attrs = {},
  children: Array<Node | string | null | undefined> = []
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);

  // Object.keys rather than Object.entries: tsconfig targets es6 with an es6 lib,
  // and Object.entries is es2017.
  for (const name of Object.keys(attrs)) {
    const value = attrs[name];
    if (value === null || value === undefined || value === false) continue;

    if (name === "class") {
      node.className = String(value);
    } else if (name === "text") {
      node.textContent = String(value);
    } else {
      node.setAttribute(name, value === true ? "" : String(value));
    }
  }

  for (const child of children) {
    if (child === null || child === undefined) continue;
    node.appendChild(typeof child === "string" ? document.createTextNode(child) : child);
  }

  return node;
}

export function clear(node: Element): void {
  while (node.firstChild) node.removeChild(node.firstChild);
}

export function replaceChildrenWith(node: Element, children: Array<Node | null | undefined>): void {
  clear(node);
  for (const child of children) {
    if (child) node.appendChild(child);
  }
}

/**
 * A polite live region. Each async effect gets its OWN region: two effects sharing
 * one node means the second announcement silently replaces the first, and a screen
 * reader user is told about the copy that succeeded instead of the export that
 * failed.
 */
export function liveRegion(testId: string): HTMLElement {
  return el("div", {
    "data-testid": testId,
    "aria-live": "polite",
    "aria-atomic": "true",
    class: "spr-live"
  });
}

export function announce(region: HTMLElement, message: string): void {
  region.textContent = message;
}

/**
 * The pair of spans a cell uses when it has nothing to show: a visible em-dash whose
 * mouseover names the absence, and the same words for a screen reader.
 *
 * One treatment for every blank in the report -- an organization nobody gave, a language
 * nobody stated, a turn nobody timed -- so a column scans as a column instead of a column
 * with italic prose in it. The words are NOT dropped: `title` on its own reaches neither a
 * keyboard user nor most screen readers, so the `.spr-sr` sibling carries them, and the
 * dash itself is aria-hidden so the absence is announced once.
 *
 * `srText` defaults to the tooltip. It differs only where the announced wording is already
 * a contract, which is "not timed" below.
 */
export function blankSpans(label: string, srText: string = label): HTMLElement[] {
  return [
    el("span", { class: "spr-untimed spr-blank", "aria-hidden": "true", title: label, text: "—" }),
    el("span", { class: "spr-sr", text: srText })
  ];
}

/**
 * The blank for "the clerk never ran the timer".
 *
 * format.ts is deliberate that "not timed" is NOT the same fact as a zero or a very short
 * turn -- see the comment on `duration()` -- so the words survive for assistive technology
 * exactly as before, and now reach a mouse reader too.
 */
export function untimedSpans(): HTMLElement[] {
  return blankSpans("Not timed", "not timed");
}

const SVG_NS = "http://www.w3.org/2000/svg";

/** One 12px glyph, drawn in currentColor so the position token on its wrapper colours it. */
function positionGlyph(kind: "for" | "against" | "neutral"): SVGElement {
  const svg = document.createElementNS(SVG_NS, "svg");
  svg.setAttribute("viewBox", "0 0 12 12");
  svg.setAttribute("width", "12");
  svg.setAttribute("height", "12");
  svg.setAttribute("aria-hidden", "true");
  svg.setAttribute("focusable", "false");
  svg.setAttribute("class", "spr-postally-icon");

  const path = document.createElementNS(SVG_NS, "path");
  // Three SHAPES, not three colours of one shape: a tick, a cross and a bar read apart in
  // greyscale and for a colour-blind reader (WCAG 1.4.1).
  path.setAttribute("d",
    kind === "for" ? "M2 6.5 4.8 9.2 10 3"
      : kind === "against" ? "M3 3 9 9M9 3 3 9"
        : "M2.5 6h7");
  svg.appendChild(path);
  return svg;
}

/**
 * For / against / neutral as three icon-and-count pairs.
 *
 * The visible pairs are aria-hidden and ONE `.spr-sr` sentence carries the reading, so a
 * screen reader hears "1 in favor, 0 opposed, 2 neutral" rather than three unnamed digits.
 * The same sentence is the group's mouseover.
 */
export function positionTally(forCount: number, against: number, neutral: number): HTMLElement {
  const words = `${forCount} in favor, ${against} opposed, ${neutral} neutral`;
  const pair = (kind: "for" | "against" | "neutral", count: number): HTMLElement =>
    el("span", { class: `spr-postally-pair spr-postally-${kind}`, "data-position-key": kind }, [
      positionGlyph(kind),
      el("span", { class: "spr-postally-n", text: String(count) })
    ]);

  return el("span", { class: "spr-postally", "data-testid": "speaker-report-position-tally", title: words }, [
    el("span", { class: "spr-postally-pairs", "aria-hidden": "true" }, [
      pair("for", forCount),
      pair("against", against),
      pair("neutral", neutral)
    ]),
    el("span", { class: "spr-sr", text: words })
  ]);
}

/** "In person: 31 of 40 (78%)". The one wording every bar segment's tooltip uses. */
export function barTip(label: string, value: number, total: number): string {
  const percent = total === 0 ? 0 : Math.round((value / total) * 100);
  return `${label}: ${value} of ${total} (${percent}%)`;
}
